import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Trash2, RotateCcw, Search, AlertTriangle, Loader2,
  User, Briefcase, FileText, DollarSign, Truck, CheckSquare,
  Target, ClipboardList, Ruler, Calendar, Zap, Package
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  contact: { icon: User, color: "bg-blue-100 text-blue-700", label: "Contact" },
  project: { icon: Briefcase, color: "bg-purple-100 text-purple-700", label: "Project" },
  quote: { icon: FileText, color: "bg-[#0F4C81]/10 text-[#0F4C81]", label: "Quote" },
  template: { icon: FileText, color: "bg-indigo-100 text-indigo-700", label: "Template" },
  price_item: { icon: DollarSign, color: "bg-emerald-100 text-emerald-700", label: "Price Item" },
  supplier: { icon: Truck, color: "bg-orange-100 text-orange-700", label: "Supplier" },
  task: { icon: CheckSquare, color: "bg-amber-100 text-amber-700", label: "Task" },
  lead: { icon: Target, color: "bg-pink-100 text-pink-700", label: "Lead" },
  inspection: { icon: ClipboardList, color: "bg-teal-100 text-teal-700", label: "Inspection" },
  measurement: { icon: Ruler, color: "bg-cyan-100 text-cyan-700", label: "Measurement" },
  event: { icon: Calendar, color: "bg-violet-100 text-violet-700", label: "Event" },
  automation: { icon: Zap, color: "bg-yellow-100 text-yellow-700", label: "Automation" },
};

function getTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function getDaysLeft(dateStr) {
  const deletedAt = new Date(dateStr).getTime();
  const autoDeleteAt = deletedAt + 90 * 24 * 60 * 60 * 1000;
  const daysLeft = Math.ceil((autoDeleteAt - Date.now()) / (24 * 60 * 60 * 1000));
  return Math.max(0, daysLeft);
}

export default function TrashPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [restoring, setRestoring] = useState(null);

  const fetchTrash = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/trash`);
      setItems(res.data);
    } catch {
      toast.error("Failed to load trash");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTrash(); }, [fetchTrash]);

  const handleRestore = async (trashId, label) => {
    setRestoring(trashId);
    try {
      await axios.post(`${API_URL}/api/trash/${trashId}/restore`);
      toast.success(`"${label}" restored`);
      setItems(prev => prev.filter(i => i.trash_id !== trashId));
    } catch {
      toast.error("Failed to restore item");
    } finally {
      setRestoring(null);
    }
  };

  const handlePermanentDelete = async (trashId, label) => {
    if (!window.confirm(`Permanently delete "${label}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/trash/${trashId}`);
      toast.success("Permanently deleted");
      setItems(prev => prev.filter(i => i.trash_id !== trashId));
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleEmptyTrash = async () => {
    if (!window.confirm(`Permanently delete ALL ${items.length} items? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/trash`);
      toast.success("Trash emptied");
      setItems([]);
    } catch {
      toast.error("Failed to empty trash");
    }
  };

  const filtered = items.filter(item => {
    if (filterType !== "all" && item.item_type !== filterType) return false;
    if (search) {
      const q = search.toLowerCase();
      return item.item_label?.toLowerCase().includes(q) || item.item_type_label?.toLowerCase().includes(q);
    }
    return true;
  });

  // Get type counts for filter badges
  const typeCounts = {};
  items.forEach(i => { typeCounts[i.item_type] = (typeCounts[i.item_type] || 0) + 1; });

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="trash-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" data-testid="trash-title">Trash</h1>
            <p className="text-sm text-slate-500">{items.length} item{items.length !== 1 ? "s" : ""} &middot; Auto-deletes after 90 days</p>
          </div>
        </div>
        {items.length > 0 && (
          <Button variant="destructive" size="sm" onClick={handleEmptyTrash} data-testid="empty-trash-btn">
            <Trash2 className="w-4 h-4 mr-1.5" /> Empty Trash
          </Button>
        )}
      </div>

      {/* Search + Filter */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search deleted items..."
            className="pl-10"
            data-testid="trash-search"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => setFilterType("all")}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${filterType === "all" ? "bg-[#0F4C81] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid="filter-all"
          >
            All ({items.length})
          </button>
          {Object.entries(typeCounts).sort((a, b) => b[1] - a[1]).map(([type, count]) => {
            const cfg = TYPE_CONFIG[type] || { label: type, color: "bg-slate-100 text-slate-700" };
            return (
              <button
                key={type}
                onClick={() => setFilterType(filterType === type ? "all" : type)}
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${filterType === type ? "bg-[#0F4C81] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                data-testid={`filter-${type}`}
              >
                {cfg.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Items */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Trash2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">{items.length === 0 ? "Trash is empty" : "No items match your search"}</p>
            <p className="text-sm text-slate-400 mt-1">{items.length === 0 ? "Deleted items will appear here for 90 days" : "Try a different search or filter"}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(item => {
            const cfg = TYPE_CONFIG[item.item_type] || { icon: Package, color: "bg-slate-100 text-slate-700", label: item.item_type };
            const Icon = cfg.icon;
            const daysLeft = getDaysLeft(item.deleted_at);

            return (
              <div
                key={item.trash_id}
                className="flex items-center gap-4 p-4 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-colors group"
                data-testid={`trash-item-${item.trash_id}`}
              >
                {/* Icon */}
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
                  <Icon className="w-4 h-4" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-800 truncate">{item.item_label}</p>
                    <Badge variant="outline" className="text-[10px] flex-shrink-0">{cfg.label}</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5">
                    <p className="text-xs text-slate-400">Deleted {getTimeAgo(item.deleted_at)}</p>
                    {daysLeft <= 7 && (
                      <span className="flex items-center gap-1 text-xs text-red-500">
                        <AlertTriangle className="w-3 h-3" /> {daysLeft}d left
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRestore(item.trash_id, item.item_label)}
                    disabled={restoring === item.trash_id}
                    className="h-8 text-xs"
                    data-testid={`restore-${item.trash_id}`}
                  >
                    {restoring === item.trash_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                    Restore
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handlePermanentDelete(item.trash_id, item.item_label)}
                    className="h-8 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-${item.trash_id}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
